import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  ArrowRight,
  Award,
  BookOpen,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  CheckSquare,
  Circle,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FileText,
  Folder,
  Layers,
  Loader2,
  MessageSquare,
  Play,
  Plus,
  PlusCircle,
  Repeat,
  Scale,
  Sparkles,
  Trophy,
  Upload,
  Users,
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { api, extractApiError } from '../../lib/api'
import RefreshFailedNotice from '../../components/RefreshFailedNotice'
import { fmtRelative, formatDayMonth, greetingName } from '../../lib/dashboard-helpers'
import { dayOffsetFromToday, formatDateTime, formatNumber, formatPattern } from '../../lib/format'
import { Avatar } from '../../components/Avatar'
import {
  Legend,
  Pie3D,
  RankedBarChart,
  TrendChart,
} from '../../components/charts/Charts'
import { DONUT_PALETTE } from '../../components/charts/palette'
import { TaskCompletionPie } from '../../components/charts/TaskCompletionPie'
import {
  myTasksResponse,
  personalDashboardResponse,
  workspaceOverviewResponse,
} from '../../lib/types'

// ============================================================================
// Shared helpers — date math, formatters, role labels
// ============================================================================

function timeOfDay() {
  // The hour on the person's own clock (their zone), not the browser's.
  const h = Number(formatPattern(new Date(), 'H'))
  if (h < 12) return 'Morning'
  if (h < 17) return 'Afternoon'
  return 'Evening'
}

function startOfDay(d) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function workRoleLabel(w) {
  if (w === 'manager') return 'Manager'
  if (w === 'team_leader') return 'Team Leader'
  return 'Employee'
}

const PRIORITY_WEIGHT = { urgent: 0, high: 1, normal: 2, low: 3 }

const PRIORITY_COLOR = {
  urgent: 'var(--color-priority-urgent)',
  high: 'var(--color-priority-high)',
  normal: 'var(--color-priority-normal)',
  low: 'var(--color-priority-low)',
}

function fmtDueChip(iso) {
  if (!iso) return { label: 'No due date', tone: 'later' }
  const due = new Date(iso)
  // Calendar days from today in the person's zone (not the browser's).
  const diffDays = dayOffsetFromToday(iso) ?? NaN
  // Spell out "day"/"days" per the project's no-shortcuts rule —
  // never "5d overdue" or "Due in 3d".
  if (diffDays < 0) {
    const n = Math.abs(diffDays)
    return { label: `${n} ${n === 1 ? 'day' : 'days'} overdue`, tone: 'overdue' }
  }
  if (diffDays === 0) return { label: 'Due today', tone: 'today' }
  if (diffDays <= 3) {
    return { label: `Due in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`, tone: 'soon' }
  }
  return {
    label: formatDateTime(due),
    tone: 'later',
  }
}

// ============================================================================
// Shared UI primitives — flat SaaS look, no gradients on cards
// ============================================================================

/** Hero — flat header that mirrors the Company Admin DashboardPage:
 *  heading + role chip + subtitle. The hero no longer carries a
 *  date stamp on the right — the global header's live clock pill
 *  already shows the current date/time, so repeating it here read
 *  as duplicate noise. No dark gradient card — sits directly on
 *  the canvas. */
function Hero({ user, tenant, role, subtitle }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-medium text-[color:var(--color-ink-strong)]">
            Good {timeOfDay()}, {greetingName(user?.fullName)}
          </h1>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-normal border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)] shrink-0">
            {/* Operations-driven Work Roles: show the assigned role's real
             *  name (e.g. a custom "Manager"); fall back to the inert tier
             *  slug label only when no Work Role is assigned. */}
            {user?.workRoleName ?? workRoleLabel(role)}
          </span>
        </div>
        <p className="mt-1 text-[color:var(--color-ink-muted)]">
          {subtitle ??
            (tenant?.name
              ? `Your work at ${tenant.name}.`
              : 'Your work at a glance.')}
        </p>
      </div>
    </div>
  )
}

function SectionHeader({ title, hint, action }) {
  // Section heading — matches the Company Admin DashboardPage exactly:
  // `font-display text-lg` with strong ink for the title, `mt-10 mb-4`
  // for the rhythm between sections. The previous treatment (small
  // uppercase eyebrow) read too quietly next to the dashboard's KPI
  // tiles and charts; the proper-heading style makes each section
  // feel like a real chapter divider and unifies the visual rhythm
  // across the dashboard.
  return (
    <div className="flex items-baseline justify-between gap-3 mt-10 mb-4">
      <div>
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h2>
        {hint ? (
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">{hint}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

// `KpiTile` (and its `KPI_TONES` palette) were removed in the May-2026
// sweep — every dashboard now uses `KpiHeroTile` (the premium variant
// further down) so the smaller component had no remaining callers.

// Shared chip colour palette — same hex values as the DashboardPage
// KPI tone map. Used by ShortcutLink so the icon chip next to a
// label/title carries a distinct hue per concept.
const CHIP_TONES = {
  gold: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
  sky: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20',
  emerald: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/20',
  amber: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-400/20',
  rose: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-400/20',
  violet: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20',
  pink: 'bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-400/20',
}

function ShortcutLink({ to, icon: Icon, label, description, badge, tone = 'sky' }) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 hover:border-[color:var(--color-accent-border)] hover:shadow-sm transition-all cursor-pointer"
    >
      <span className={`h-12 w-12 rounded-xl ${CHIP_TONES[tone]} flex items-center justify-center shrink-0`}>
        <Icon size={24} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
            {label}
          </p>
          {badge ? (
            <span className="text-[13px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border border-[color:var(--color-info-border)] inline-flex items-center gap-1">
              <Sparkles size={9} />
              {badge}
            </span>
          ) : null}
        </div>
        <p className="text-[13px] text-[color:var(--color-ink-muted)]">{description}</p>
      </div>
      <ArrowRight
        size={14}
        className="text-[color:var(--color-ink-subtle)] transition-transform group-hover:translate-x-0.5"
      />
    </Link>
  )
}

// `PrimaryCta` and `EmptyCard` were removed in the May-2026 sweep —
// every dashboard moved to the shared `Button` primitive for the
// headline action, and the EmptyCard primitive was only consumed by
// `TopPerformersList`, which itself was removed in the same pass.

function DueChip({ iso }) {
  const { label, tone } = fmtDueChip(iso)
  const cls =
    tone === 'overdue'
      ? 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border-[color:var(--color-danger-border)]'
      : tone === 'today'
        ? 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]'
        : tone === 'soon'
          ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
          : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]'
  return (
    <span
      className={
        'inline-flex items-center px-2 py-0.5 rounded-full border text-[13px] font-normal tabular-nums shrink-0 ' +
        cls
      }
    >
      {label}
    </span>
  )
}

/** Tiny task row — used by the personal "Today's Focus" list.
 *  Compact: priority dot, title, breadcrumb, due chip. */
function TaskRow({ task }) {
  return (
    <Link
      to={`/app/spaces/${task.spaceId}/lists/${task.listId}`}
      className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
    >
      {task.priority ? (
        <span
          className="h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: PRIORITY_COLOR[task.priority] }}
          aria-label={`Priority ${task.priority}`}
        />
      ) : (
        <span
          className="h-2 w-2 rounded-full shrink-0 bg-[color:var(--color-rule-strong)]"
          aria-hidden="true"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-normal text-[color:var(--color-ink-strong)] truncate">
          {task.name}
        </p>
        <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate">
          {task.spaceName} · {task.listName}
        </p>
      </div>
      <DueChip iso={task.dueDate} />
      <ArrowRight size={14} className="text-[color:var(--color-ink-subtle)] shrink-0" />
    </Link>
  )
}

// ============================================================================
// Analytics primitives — donut, vertical bars, horizontal bars, progress ring.
// Pure SVG/CSS so we don't pull in a charting library for what amount to a
// handful of summary widgets.
// ============================================================================

// ============================================================================
// Removed in the May-2026 sweep: AnalyticsCard / Donut / VerticalBars /
// HBars / FooterStats. The chart cards each render bespoke SVG / list
// markup inline now, and the AnalyticsCard wrapper was superseded by the
// per-card scaffolding inside the dashboard's team section.
// ============================================================================

/** Tone palette for KpiHeroTile — tinted background + vivid mid-tone
 *  glyph + matching halo gradient. Mirrors the Company Admin variant. */
const TONE_CLASSES_HERO = {
  gold: {
    tile:
      'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
    halo: 'from-[#FEF3C7] to-transparent dark:from-transparent',
  },
  sky: {
    tile:
      'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile:
      'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/20',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile:
      'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-400/20',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  rose: {
    tile:
      'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-400/20',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
  violet: {
    tile:
      'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  pink: {
    tile:
      'bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-400/20',
    halo: 'from-[#FCE7F3] to-transparent dark:from-transparent',
  },
  slate: {
    tile:
      'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
    halo: 'from-[color:var(--color-canvas)] to-transparent',
  },
}

// Sparkline / smoothPath / TONE_STROKE removed 2026-05-22 along with
// the per-tile sparkline strip — see KpiHeroTile below. If sparklines
// are ever brought back, the prior implementation lives in git history.

// `DeltaChip` (week-over-week ▲/▼/— indicator) was removed alongside
// the `delta` prop on `KpiHeroTile`. Restore from git history if a
// future smarter chip wants to surface period deltas.

/** Hero KPI tile — premium strip member. Shows label + headline
 *  number + optional muted hint + optional "View →" link. Shares
 *  tone palette + halo behaviour with the rest of the dashboard so
 *  the row reads as one family.
 *
 *  Matches the Company Admin Dashboard's `KpiHeroTile` shape exactly
 *  (same content stack, same `p-5` chrome, no extra slots) so the
 *  strips render at identical heights when stacked. */
function KpiHeroTile({ label, value, icon: Icon, tone, to, hint }) {
  // Sparkline support removed 2026-05-22 — a single user's daily
  // task activity is too sparse for a 14-day mini-trend to carry
  // signal; the curves were nearly always flat baselines and read as
  // rendering bugs. Icon chip + tone-tinted halo blob continue to give
  // each tile its colour identity.
  const tones = TONE_CLASSES_HERO[tone] ?? TONE_CLASSES_HERO.slate
  const body = (
    <>
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* `min-h-[2.5rem]` reserves vertical space for a 2-line
           *  label so the value sits at the same y-coordinate across
           *  every tile in the row — even when one tile's label
           *  wraps ("Reports Submitted · This Week") and the others
           *  don't ("Team Members", "Team Tasks Open"). Without
           *  this floor the wrap pushed the value down on the long-
           *  label tile and the row read as misaligned. */}
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 min-h-[2.5rem] leading-tight">
            {label}
          </h3>
          <p className="mt-2 font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {/* Secondary line — muted hint string. The delta-chip slot
           *  that used to live below this hint was removed so the
           *  tile's content height matches the Company Admin's
           *  `KpiHeroTile` exactly. */}
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">{hint}</p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
      {/* "View →" pinned to the card's bottom edge via `mt-auto`.
       *  CSS Grid stretches the tiles in each row to the tallest
       *  natural height (set by whichever tile has the longest
       *  wrapping label — usually "Reports Submitted · This Week").
       *  Without `mt-auto` the View link on the shorter tiles sat
       *  directly under the value and the row read as misaligned.
       *  Bottom-anchored Views align across the whole row. */}
      {to ? (
        <div className="relative mt-auto pt-3 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] opacity-80 group-hover:opacity-100">
          View
          <ArrowRight
            size={12}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </div>
      ) : null}
    </>
  )
  // CSS Grid stretches the siblings in each row to the tallest
  // natural height, so no fixed `min-h-*` is needed here — and
  // omitting it lets the row sit tight when every tile carries the
  // same minimal content (label + value + View).
  return to ? (
    <Link
      to={to}
      className="group flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
    >
      {body}
    </Link>
  ) : (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      {body}
    </div>
  )
}

// ─── Team Analytics row cards ───────────────────────────────────────
//
// `WorkloadBySpaceCard` (and the cards below) power the dashboard's team
// Analytics section, from fields the backend ships on
// `workspaceOverviewResponse`. They reuse the shared chart primitives from
// `components/charts/Charts.jsx` so the visual rhythm matches the Company
// Admin Dashboard.

/** Workload by Space ranked bars. Reads `tasks.bySpace` (top 8 spaces
 *  by open count, sorted desc on the backend), drawn like the dashboards'
 *  "Tasks by Department" chart — Space names on the left, a value axis
 *  underneath — with slimmer bars (on request, 2026-10-07). */
function WorkloadBySpaceCard({ bySpace, otherSpacesOpen = 0 }) {
  const rows = (bySpace ?? [])
    .filter((s) => (s.count ?? 0) > 0)
    .map((s) => ({ label: s.name, value: s.count }))
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.violet.tile}`}>
            <Folder size={20} />
          </span>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            Workload by Space
          </h3>
        </div>
        <span className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
          {rows.length} space{rows.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="flex-1">
        <RankedBarChart
          rows={rows}
          size="thin"
          formatValue={(n) => `${formatNumber(n)} open`}
          emptyMessage="No open tasks across the team's Spaces."
        />
      </div>
      {/* [QA-20] Team work in Spaces you can't open is counted (not named),
       *  so these bars add up to the team's open tasks. */}
      {otherSpacesOpen > 0 ? (
        <p className="mt-3 text-[13px] text-[color:var(--color-ink-muted)]">
          +{formatNumber(otherSpacesOpen)} open in Spaces you can&apos;t open
        </p>
      ) : null}
    </div>
  )
}

// ─── Phase 4 team-view additions ────────────────────────────────────
//
// A scope indicator + the Team by Work Role / department Task Completion
// cards for the supervision view, all fed by existing /me/workspace-overview
// fields.

/** "Company-wide" vs "Your department" indicator for the team view. Keyed to
 *  the supervision scope the backend reports (`company` for a Company Admin
 *  or a holder of `tasks.view_workspace`), so it matches the member widgets,
 *  the hero tiles and the Team Tasks page they link to. */
function ScopePill({ scope }) {
  const company = scope === 'company'
  const Icon = company ? Layers : Users
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[13px] font-medium border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)]">
      <Icon size={12} />
      {company ? 'Company-wide' : 'Your department'}
    </span>
  )
}

/** Non-repeat / Repeat Task Completion 3D pie — the Company Admin dashboard's
 *  pair, scoped to the viewer rather than the whole company. `part` is one
 *  side of a `tasks.repeatSplit`:
 *    - team view: me.service workspaceOverview — the Team Tasks feed narrowed
 *      to the department rule (`scope` 'self' when the viewer has no
 *      department view);
 *    - self-only view: me.service dashboardOverview — the user's own tasks
 *      (`scope` 'self').
 *  The meta line says whose tasks these are, never implying a department. */
const REPEAT_SPLIT_SCOPE_LABEL = {
  department: 'Your department',
  self: 'Your tasks',
  all: 'All tasks',
}
function RepeatSplitCard({ title, part, scope, icon: Icon, tone, empty }) {
  const total = part?.total ?? 0
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO[tone].tile}`}>
            <Icon size={20} />
          </span>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            {title}
          </h3>
        </div>
        <span className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
          {REPEAT_SPLIT_SCOPE_LABEL[scope] ?? REPEAT_SPLIT_SCOPE_LABEL.department} ·{' '}
          {formatNumber(total)} total
        </span>
      </div>
      <TaskCompletionPie part={part} empty={empty} />
    </div>
  )
}

/** Team by Work Role 3D pie — the active roster grouped by Work Role, drawn
 *  like the Task Completion pies beside it (Pie3D: taller slice = more
 *  members, badge callouts with each share). A member with no Work Role
 *  linked is counted under their level (e.g. "Employee"), as on the Members
 *  page — never a separate "Unassigned" slice (server:
 *  utils/work-role-breakdown.js). Work-role colours aren't on the wire, so
 *  fall back to the shared chart palette. The header total is Σ byWorkRole
 *  (role'd internal members), which can be ≤ the Team Members tile when
 *  admins/externals are in scope — so it is labelled by its own total, never
 *  equated to members.total. */
function TeamWorkRoleCard({ byWorkRole }) {
  const segments = (byWorkRole ?? []).map((r, i) => ({
    label: r.name,
    value: r.count ?? 0,
    color: DONUT_PALETTE[i % DONUT_PALETTE.length],
  }))
  const total = segments.reduce((s, x) => s + (x.value ?? 0), 0)
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.sky.tile}`}>
            <Users size={20} />
          </span>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            Team by Work Role
          </h3>
        </div>
        {total > 0 ? (
          <span className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
            {formatNumber(total)} member{total === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      {total === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
          No role breakdown available yet.
        </p>
      ) : (
        <div className="flex flex-col gap-4 py-1">
          <Pie3D segments={segments} width={340} />
          <Legend
            segments={segments}
            formatValue={(v, pct) => `${formatNumber(v)} · ${pct}%`}
          />
        </div>
      )}
    </div>
  )
}

// ─── Team Momentum cards ────────────────────────────────────────────
//
// Two cards fed by fields /me/workspace-overview already ships, all scoped
// server-side to the viewer's supervised team (memberIds) — no new data:
//   Top Performers  ← topPerformersLast30 (top 5 by net points, 30 days)
//   Team Throughput ← pointsLast30, tasks.completedThisWeek/LastWeek,
//                     reportsLast30 / reportsThisWeek, badgeCount
// Plain figures with a static muted hint — no % delta chips, which the
// Company Admin dashboard dropped as noise against zero baselines.

/** Top Performers leaderboard — the Company Admin "Top Performers" row look
 *  (rank pill with gold #1 + trophy, avatar, name, department, points), plus
 *  a bar scaled to the leader and each member's share of the team's 30-day
 *  points. Rows open Team Overview, as the old feed rows did. */
function TeamTopPerformersCard({ performers, teamPoints, currentUserId, showLeaderboardLink }) {
  const rows = (performers ?? []).slice(0, 5)
  const leader = Math.max(1, ...rows.map((r) => r.points ?? 0))
  const team = Math.max(0, teamPoints ?? 0)
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col overflow-hidden">
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.gold.tile}`}>
            <Trophy size={20} />
          </span>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            Top Performers
          </h3>
        </div>
        <span className="text-[13px] text-[color:var(--color-ink-muted)] shrink-0">Last 30 days</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center flex-1">
          No points earned in the last 30 days yet.
        </p>
      ) : (
        <ol className="m-0 p-0 list-none divide-y divide-[color:var(--color-rule)] -mx-6 flex-1">
          {rows.map((r, i) => {
            const rank = r.rank ?? i + 1
            const isTop = rank === 1
            const points = Math.max(0, r.points ?? 0)
            const share = team > 0 ? Math.round((points / team) * 100) : null
            const isMe = currentUserId != null && String(r.userId) === String(currentUserId)
            return (
              <li key={r.userId ?? `${r.fullName}-${i}`}>
                <Link
                  to="/app/team-overview"
                  className="flex items-center gap-3 px-6 py-3 hover:bg-[color:var(--color-card-hover)] transition-colors"
                >
                  <span
                    className={
                      'inline-flex items-center justify-center h-6 min-w-[28px] px-1.5 rounded-full text-[13px] font-display tabular-nums border shrink-0 ' +
                      (isTop
                        ? 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20'
                        : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]')
                    }
                  >
                    #{rank}
                  </span>
                  <Avatar name={r.fullName} avatarUrl={r.avatarUrl ?? null} size="md" colorByName />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="m-0 min-w-0 flex items-center gap-1.5 text-sm text-[color:var(--color-ink-strong)]">
                        <span className="font-medium truncate">{r.fullName}</span>
                        {isMe ? (
                          <span className="shrink-0 px-1.5 rounded text-[11px] font-medium uppercase tracking-wider bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                            You
                          </span>
                        ) : null}
                        {isTop ? <Trophy size={13} className="shrink-0 text-[#D97706] dark:text-amber-300" aria-label="Top performer" /> : null}
                      </p>
                      <span className="font-display text-sm text-[color:var(--color-ink-strong)] tabular-nums shrink-0">
                        {formatNumber(points)} pts
                      </span>
                    </div>
                    <p className="m-0 mt-0.5 text-[13px] text-[color:var(--color-ink-muted)] truncate">
                      {[r.departmentName, share != null ? `${share}% of team points` : null].filter(Boolean).join(' · ') || '—'}
                    </p>
                    {/* Decorative: the visible "N pts" / "% of team points"
                     *  text already carries the value, so screen readers
                     *  don't hear the name and points twice per row. */}
                    <div
                      className="mt-2 h-1.5 rounded-full bg-[color:var(--color-rule)] overflow-hidden"
                      aria-hidden="true"
                    >
                      <div
                        className="h-full rounded-full transition-[width] duration-500"
                        style={{ width: `${(points / leader) * 100}%`, background: 'var(--color-accent)' }}
                      />
                    </div>
                  </div>
                </Link>
              </li>
            )
          })}
        </ol>
      )}
      {showLeaderboardLink ? (
        <Link
          to="/app/leaderboard"
          className="mt-3 self-start inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline"
        >
          View full leaderboard
          <ArrowRight size={12} />
        </Link>
      ) : null}
    </div>
  )
}

/** One Team Throughput figure — tone chip + label, headline number, and a
 *  muted context line. A link only when `to` is given (the target page is
 *  gated by the caller). */
function MomentumStat({ icon: Icon, tone, label, value, hint, to }) {
  const tones = TONE_CLASSES_HERO[tone] ?? TONE_CLASSES_HERO.slate
  const body = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tones.tile}`}>
          <Icon size={16} />
        </span>
        <span className="min-w-0 truncate text-[13px] uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium leading-tight">
          {label}
        </span>
      </div>
      <p
        className="m-0 mt-3 min-w-0 truncate font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none"
        title={formatNumber(value ?? 0)}
      >
        {formatNumber(value ?? 0)}
      </p>
      {/* Arrow is inline (not a flex item) so it follows the last word
       *  when the hint wraps on a narrow screen. */}
      <p className="m-0 mt-1.5 text-[13px] text-[color:var(--color-ink-muted)]">
        {hint}
        {to ? (
          <ArrowRight size={12} className="inline-block align-[-1px] ml-1 text-[color:var(--color-accent-strong)]" aria-hidden="true" />
        ) : null}
      </p>
    </>
  )
  const cls = 'rounded-lg bg-[color:var(--color-canvas)] p-4 flex flex-col'
  return to ? (
    <Link to={to} className={`${cls} hover:bg-[color:var(--color-card-hover)] transition-colors`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

/** Team Throughput — the team's 30-day points, this week's completions (vs
 *  last week), report submissions, and badges, as plain figures. */
function TeamThroughputCard({ overview, reportsHref }) {
  const tasks = overview?.tasks ?? {}
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.emerald.tile}`}>
          <Activity size={20} />
        </span>
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          Team Throughput
        </h3>
      </div>
      {/* 4-up while the card is full width (below xl, where the two
       *  Momentum cards stack), 2×2 in the narrower xl side column — so a
       *  tile is never too narrow for its label row. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-2 gap-3 flex-1">
        <MomentumStat
          icon={Sparkles}
          tone="gold"
          label="Points"
          value={Math.max(0, overview?.pointsLast30 ?? 0)}
          hint="earned in 30 days"
        />
        <MomentumStat
          icon={CheckCircle2}
          tone="emerald"
          label="Completed"
          value={tasks.completedThisWeek ?? 0}
          hint={`this week · ${formatNumber(tasks.completedLastWeek ?? 0)} last week`}
        />
        <MomentumStat
          icon={ClipboardList}
          tone="sky"
          label="Reports"
          value={overview?.reportsLast30 ?? 0}
          hint={`in 30 days · ${formatNumber(overview?.reportsThisWeek ?? 0)} this week`}
          to={reportsHref}
        />
        <MomentumStat
          icon={Award}
          tone="violet"
          label="Badges"
          value={overview?.badgeCount ?? 0}
          hint="earned, all time"
        />
      </div>
    </div>
  )
}

// ============================================================================
// User Dashboard — a SINGLE adaptive page whose surfaces are shown or
// hidden purely by the signed-in user's Operations Permissions.
//
// There is no longer a per-tier dispatcher (Manager / Team Leader /
// Employee). Every user lands on the same component; the personal
// section is always present, and each team / supervision widget is
// gated on the specific operation that authorises its underlying data.
// company_admin / super_admin / sub_admin short-circuit every operation
// check inside `hasOperation`, so they see the full set.
// ============================================================================

/** Renders a tenant-local `YYYY-MM-DD` day-key as a calendar date.
 *  Constructed WITHOUT a `Z` suffix so the browser shows the same
 *  calendar day regardless of its own timezone. */
function fmtDayKeyLong(key) {
  if (!key) return ''
  // A "YYYY-MM-DD" key is a calendar day — formatPattern never shifts it.
  return formatPattern(key, 'EEE d MMM') || key
}

function fmtDayKeyShort(key) {
  if (!key) return ''
  return formatPattern(key, 'd MMM') || key
}

/** One weekly / monthly cadence row on the Daily Report card. Rendered
 *  only once the cadence's reminder date arrives (due today, or past the
 *  due day within the same period): a submit prompt until the report is
 *  filed, then a compact green confirmation. */
function CadenceReminder({ icon: Icon, label, reminder, to }) {
  if (!reminder) return null
  const { dueToday, pastDue, submitted, periodStart, periodEnd } = reminder
  if (!dueToday && !pastDue) return null
  const period = `${fmtDayKeyShort(periodStart)} – ${fmtDayKeyShort(periodEnd)}`
  if (submitted) {
    return (
      <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] dark:border-emerald-500/20 dark:bg-emerald-500/[0.07] px-4 py-3 flex items-center gap-2.5">
        <CheckCircle2
          size={16}
          className="text-[#059669] dark:text-emerald-300 shrink-0"
        />
        <p className="text-[13px] text-[#065F46] dark:text-emerald-200 m-0 min-w-0">
          {label} report submitted{' '}
          <span className="text-[color:var(--color-ink-muted)]">({period})</span>
        </p>
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-4">
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-full bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20 flex items-center justify-center shrink-0">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
            {label} report {dueToday ? 'is due today' : "hasn't been submitted yet"}
          </p>
          <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)]">
            Covers {period}.
          </p>
        </div>
      </div>
      <Link
        to={to}
        className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-medium bg-[color:var(--color-primary)] text-white shadow-sm hover:shadow hover:bg-[color:var(--color-primary-hover)] transition-all cursor-pointer"
      >
        <FileText size={13} />
        Submit {label} Report
      </Link>
    </div>
  )
}

// ─── Self-only "Your Numbers" cards ─────────────────────────────────
//
// Personal analytics for the self-only (non-supervisor) view. Every card
// is fed by data the dashboard ALREADY fetches — `personalDashboardResponse`
// (the pre-aggregated, tenant-local overview) and the `myTasksResponse`
// feed. No extra API calls, no team/company data, and completion is always
// Boolean(completedAt) (so cancelled counts as done, matching admin
// analytics) — never a status-name match. Styling mirrors the team
// Analytics cards above so the visual rhythm matches the rest of the app.

/** Shared card chrome for the personal analytics cards: icon chip + title +
 *  optional right-aligned meta, then the body fills the remaining height. */
function PersonalCardShell({ icon: Icon, tone = 'sky', title, meta, children }) {
  const tones = TONE_CLASSES_HERO[tone] ?? TONE_CLASSES_HERO.sky
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${tones.tile}`}>
            <Icon size={20} />
          </span>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
            {title}
          </h3>
        </div>
        {meta ? (
          <span className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
            {meta}
          </span>
        ) : null}
      </div>
      <div className="flex-1 flex flex-col">{children}</div>
    </div>
  )
}

/** Workload by Space — my OPEN tasks grouped by Space, top 8 by count. */
function MySpaceCard({ rows, loading, error }) {
  return (
    <PersonalCardShell
      icon={Folder}
      tone="amber"
      title="Workload by Space"
      meta={!error && rows.length > 0 ? `${rows.length} space${rows.length === 1 ? '' : 's'}` : undefined}
    >
      {error ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
          {error}
        </p>
      ) : loading && rows.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-6">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </p>
      ) : (
        // Same ranked-bar look as the dashboards' "Tasks by Department"
        // chart, with slimmer bars (on request, 2026-10-07).
        <RankedBarChart
          rows={rows}
          size="thin"
          formatValue={(n) => `${formatNumber(n)} open`}
          emptyMessage="No open tasks across your Spaces."
        />
      )}
    </PersonalCardShell>
  )
}

/** My Last 14 Days — created-vs-completed trend from the (tenant-local)
 *  sparklines. TrendChart renders a clean empty state when every day is 0. */
function MyMomentumCard({ trend, error }) {
  return (
    // `h-full` matches its row partner (Workload by Space); the header wraps
    // so the legend drops under the title in a half-width column.
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 h-full flex flex-col">
      <div className="flex flex-wrap items-baseline justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.sky.tile}`}>
            <Activity size={20} />
          </span>
          <div>
            <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
              My Last 14 Days
            </h3>
            <p className="text-[13px] text-[color:var(--color-ink-muted)]">
              {/* [QA-23] The second series is NEW tasks on your list (assigned to
               *  you or created by you) — it was labelled as tasks you created. */}
              Tasks you completed vs. new tasks on your list, day by day
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[13px] text-[color:var(--color-ink-muted)] shrink-0">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />
            Completed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--color-accent)' }} />
            New
          </span>
        </div>
      </div>
      {/* [QA-25] A failed overview call is an error, not "no activity". */}
      {error && trend.labels.length === 0 ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
          {error}
        </p>
      ) : (
        <TrendChart
          labels={trend.labels}
          series={trend.series}
          labelFormat={formatDayMonth}
          emptyMessage="No task activity in the last 14 days."
        />
      )}
    </div>
  )
}

export function UserHomePage() {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)

  // ── Operation booleans (mirror the naming UserLayout.jsx uses) ──────
  // These drive every visibility decision below. No Work Role names or
  // tier slugs are consulted anywhere — only operations.
  // Member Directory (Team Overview) is reachable with EITHER the page op OR a
  // member-directory scope op — mirrors the backend `me.team` gate and the
  // reports/leaderboard combining pattern below, so "View member directory"
  // alone surfaces the page.
  const canSeeTeamOverview = useAuthStore((s) =>
    s.hasAnyOperation([
      'pages.team_overview',
      'members.view_workspace',
      'members.view_department',
    ]),
  )
  const canSeeTeamTasks = useAuthStore((s) => s.hasOperation('pages.team_tasks'))
  const canSeeTeamReports = useAuthStore((s) => s.hasOperation('pages.team_reports'))
  const canSeePerformance = useAuthStore((s) => s.hasOperation('pages.performance'))
  const canViewMembers = useAuthStore((s) =>
    s.hasAnyOperation(['members.view_department', 'members.view_workspace']),
  )
  const canViewReportsScope = useAuthStore((s) =>
    s.hasAnyOperation(['reports.view_department', 'reports.view_workspace']),
  )
  const canViewLeaderboardScope = useAuthStore((s) =>
    s.hasAnyOperation(['leaderboard.view_department', 'leaderboard.view_workspace']),
  )
  const canDelegate = useAuthStore((s) => s.hasOperation('tasks.delegate'))

  const canViewTeamReports = canSeeTeamReports || canViewReportsScope
  const canViewLeaderboard = canSeePerformance || canViewLeaderboardScope

  // ── Data fetching ───────────────────────────────────────────────────

  // Personal overview — backs the whole personal section (KPI strip,
  // mentions feed, daily-report status, SOP steps, recent activity).
  // Not permission-gated: every user has personal data.
  const personalQuery = useQuery({
    queryKey: ['me-dashboard-overview'],
    queryFn: async () => {
      const res = await api.get('/me/dashboard-overview')
      return personalDashboardResponse.parse(res.data)
    },
    enabled: Boolean(user),
    staleTime: 60_000,
    // Overdue / Due Today are time-dependent (server-computed) — poll once a
    // minute so the personal KPI tiles stay live as tasks cross their due
    // point. Matches the workspace-overview query below.
    refetchInterval: 60_000,
  })
  const personal = personalQuery.data ?? null
  // [QA-14] A blocking error only when there is NO data. React Query keeps the
  // last good data on a failed background refetch and just flips isError;
  // treating that as fatal blanked cards that still had good numbers. The
  // refetch failure is surfaced once, non-blocking (RefreshFailedNotice).
  const personalError = personalQuery.isError && !personal
    ? extractApiError(personalQuery.error).message
    : null

  // My-tasks feed — every user has assigned tasks. Always enabled.
  const tasksQuery = useQuery({
    queryKey: ['my-tasks'],
    queryFn: async () => {
      const res = await api.get('/me/tasks')
      return myTasksResponse.parse(res.data)
    },
    enabled: Boolean(user),
  })
  const myTasks = useMemo(() => {
    const all = tasksQuery.data?.data ?? []
    if (!user) return []
    return all.filter((t) => t.assigneeIds.includes(user.id))
  }, [tasksQuery.data, user])

  // Full /me/tasks population — assigned-to-me ∪ created-by-me, the SAME set
  // the backend computes openCount / overdueCount / dueTodayCount over. The
  // "Your Numbers" analytics cards derive from THIS (not the assigned-only
  // `myTasks` above) so their open/total figures reconcile with the Open
  // Workload donut and the KPI tiles; `myTasks` stays assigned-only for the
  // Today's Focus / Recent Activity lists.
  const myTaskRows = useMemo(() => tasksQuery.data?.data ?? [], [tasksQuery.data])

  // Team / workspace overview — gated. The backend 403s without
  // `pages.team_overview`, so the query MUST stay disabled for users
  // who lack it; otherwise the dashboard would fire a request it
  // can't satisfy on every mount.
  const overviewQuery = useQuery({
    queryKey: ['workspace-overview'],
    queryFn: async () => {
      const res = await api.get('/me/workspace-overview')
      return workspaceOverviewResponse.parse(res.data)
    },
    enabled: Boolean(user) && canSeeTeamOverview,
    refetchInterval: 60_000,
  })
  const overview = overviewQuery.data ?? null

  // "Assign Task" quick action should land on a real Space list page,
  // where the "+ Create Task" form lives. The dashboard has no single
  // canonical list, so derive one from tasks already in scope (the
  // user's own assignments first, then any team task), and fall back to
  // the Team Tasks page if no list can be resolved.
  const assignTaskHref = useMemo(() => {
    const pools = [myTasks, overview?.overdueTopTasks ?? []]
    for (const pool of pools) {
      const t = pool.find((x) => x.spaceId && x.listId)
      if (t) return `/app/spaces/${t.spaceId}/lists/${t.listId}`
    }
    return '/app/team-tasks'
  }, [myTasks, overview])

  // ── Loading / error flags ───────────────────────────────────────────
  // [QA-14] Blocking only without data — see `personalError`.
  const tasksLoading = tasksQuery.isLoading
  const tasksError = tasksQuery.isError && !tasksQuery.data
    ? extractApiError(tasksQuery.error).message
    : null
  const teamLoading = overviewQuery.isLoading
  const teamError = overviewQuery.isError && !overview
    ? extractApiError(overviewQuery.error).message
    : null
  // Queries whose background refetch failed while their last good data is
  // still on screen — one non-blocking notice covers them all.
  const staleQueries = [personalQuery, tasksQuery, overviewQuery].filter(
    (q) => q.isError && q.data,
  )

  // ── Personal derivations ────────────────────────────────────────────
  const upNext = useMemo(() => sortUpNext(myTasks).slice(0, 5), [myTasks])

  // Recently-assigned + completed-today derivations feed the personal
  // Recent Activity feed. `recentlyAssigned` is sorted by createdAt
  // desc (newest first) and capped at 3; `completedToday` filters by
  // completedAt within the local-day boundary and caps at 3.
  const recentlyAssigned = useMemo(() => {
    return [...myTasks]
      .filter((t) => t.createdAt)
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
      .slice(0, 3)
  }, [myTasks])

  const completedToday = useMemo(() => {
    const today0 = startOfDay(new Date()).getTime()
    return myTasks
      .filter(
        (t) =>
          t.completedAt && new Date(t.completedAt).getTime() >= today0,
      )
      .sort(
        (a, b) =>
          new Date(b.completedAt).getTime() -
          new Date(a.completedAt).getTime(),
      )
      .slice(0, 3)
  }, [myTasks])

  const dailyReport = personal?.dailyReportStatus ?? null
  // Weekly / monthly cadence prompts for the same card — `null` until
  // the server includes them (older deploys) or for non-user roles.
  const reportReminders = personal?.reportReminders ?? null
  // Memoised so its array identity is stable across renders — it feeds
  // the `activityFeed` useMemo dependency list below.
  const sopSteps = useMemo(() => personal?.sopSteps ?? [], [personal])

  // Personal Recent Activity feed — zips together newly assigned
  // tasks, tasks completed today, mentions, and SOP steps that have
  // moved into in_progress. Capped at 8 rows.
  const activityFeed = useMemo(() => {
    if (!user) return []
    const items = []
    recentlyAssigned.forEach((t) => {
      items.push({
        id: `assigned-${t.id}`,
        ts: t.createdAt ?? null,
        href: `/app/spaces/${t.spaceId}/lists/${t.listId}`,
        primary: t.name,
        secondary: 'was assigned to you',
        icon: Plus,
        tone: 'sky',
      })
    })
    completedToday.forEach((t) => {
      items.push({
        id: `done-${t.id}`,
        ts: t.completedAt ?? null,
        href: `/app/spaces/${t.spaceId}/lists/${t.listId}`,
        primary: t.name,
        secondary: 'marked complete',
        icon: CheckCircle2,
        tone: 'emerald',
      })
    })
    if (personal) {
      personal.mentions.slice(0, 3).forEach((m) => {
        const taskName =
          m.taskName.length > 40 ? m.taskName.slice(0, 40) + '…' : m.taskName
        // Label by the real action — mention vs reply vs plain comment.
        const action =
          m.type === 'reply'
            ? 'replied to your comment'
            : m.type === 'comment'
              ? 'commented'
              : 'mentioned you'
        items.push({
          id: `mention-${m.id}`,
          ts: m.createdAt ?? null,
          href: '/app/notifications',
          primary: `${m.fromUserName} ${action}`,
          secondary: `on ${taskName}`,
          icon: MessageSquare,
          tone: 'violet',
          avatarName: m.fromUserName,
          avatarUrl: m.fromAvatarUrl,
        })
      })
    }
    sopSteps
      .filter((s) => s.status === 'in_progress')
      .slice(0, 2)
      .forEach((s) => {
        items.push({
          id: `sop-${s.stepId}`,
          ts: null,
          href: s.taskId
            ? `/app/focus-board?taskId=${s.taskId}`
            : '/app/sops',
          primary: s.stepTitle,
          secondary: `in progress on ${s.sopName}`,
          icon: BookOpen,
          tone: 'amber',
        })
      })
    // Sort newest first; rows without a ts sink to the bottom.
    items.sort((a, b) => {
      const at = a.ts ? new Date(a.ts).getTime() : 0
      const bt = b.ts ? new Date(b.ts).getTime() : 0
      return bt - at
    })
    return items.slice(0, 8)
  }, [user, recentlyAssigned, completedToday, personal, sopSteps])

  // ── Personal analytics derivations (self-only "Your Numbers") ───────
  // All derived from data the dashboard already fetches — no extra calls.
  // Computed unconditionally (rules of hooks); only RENDERED in the
  // self-only branch.

  // Workload by Space — my OPEN tasks grouped by Space, top 8 by count. Built
  // from the full assigned ∪ created set (myTaskRows), the same population as
  // the Overdue / To Do tiles, rather than the assigned-only subset.
  const myBySpace = useMemo(() => {
    const counts = new Map()
    for (const t of myTaskRows) {
      if (t.completedAt) continue // open only
      const name = t.spaceName || 'No Space'
      counts.set(name, (counts.get(name) ?? 0) + 1)
    }
    return [...counts.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)
  }, [myTaskRows])

  // 14-day created-vs-completed trend — straight from the (now tenant-local)
  // sparklines. Keyed off the completed series' day axis; createdSparkline
  // shares the same 14-day keys.
  const myTrend = useMemo(() => {
    const completedSpark = personal?.tasks?.completedSparkline ?? []
    const createdSpark = personal?.tasks?.createdSparkline ?? []
    return {
      labels: completedSpark.map((p) => p.day),
      series: [
        {
          name: 'Completed',
          color: 'var(--color-success)',
          data: completedSpark.map((p) => p.count ?? 0),
        },
        {
          name: 'New',
          color: 'var(--color-accent)',
          data: createdSpark.map((p) => p.count ?? 0),
        },
      ],
    }
  }, [personal])

  // ── Team derivations ────────────────────────────────────────────────
  // All computed unconditionally (rules of hooks); only RENDERED when
  // the relevant operation gate is true.

  // Team Momentum — the "View full leaderboard" link follows the sidebar's
  // Leaderboard entry, which is shown only while the plan has gamification.
  const hasGamification = useAuthStore((s) => s.hasFeature('gamification'))

  // ── Section-visibility helpers ──────────────────────────────────────
  // Only render a SectionHeader when at least one widget under it is
  // visible, so we never leave an empty heading on the page.
  const showActionNeeded = canSeeTeamOverview || canViewTeamReports || canViewMembers
  const showAnalytics = canSeeTeamOverview
  const showTeamMomentum = canViewLeaderboard
  const showTeamQuickActions = canSeeTeamTasks || canDelegate

  return (
    <div>
      {/* No breadcrumb here — a root page's crumb is just its own
       *  non-clickable name, duplicating the sidebar's active state
       *  (the Super Admin dashboard already omits it; QA flagged the
       *  inconsistency). Sub-pages keep their real trails. */}
      <Hero
        user={user}
        tenant={tenant}
        role="employee"
        subtitle={
          canSeeTeamOverview
            ? // Scope-neutral — the "Company-wide / Your department" pill on
              // the Team Supervision header states the exact scope, so the hero
              // no longer implies company-wide with "across {tenant}".
              'A live view of the team you oversee.'
            : undefined
        }
      />

      {/* [QA-14] A failed background refetch keeps the last good numbers on
       *  screen; say so once instead of blanking the sections. */}
      {staleQueries.length > 0 ? (
        <RefreshFailedNotice
          error={staleQueries[0].error}
          onRetry={() => staleQueries.forEach((q) => q.refetch())}
        />
      ) : null}

      {/* ════════════════════════════════════════════════════════════════
       *  PERSONAL SECTION — always rendered. Every user has personal
       *  data, so none of this is gated.
       *  ══════════════════════════════════════════════════════════════ */}

      {/* ──────────────────────────────────────────────────────────────────
       *  My Work Today — hero KPI strip: Due Today / Overdue / To Do /
       *  Completed Tasks. Action-first so the leading tiles answer "what
       *  should I do next?". (Current Streak was replaced by To Do +
       *  Completed Tasks on request, 2026-10-07.)
       *  ───────────────────────────────────────────────────────────── */}
      {/* PERSONAL SECTION — shown ONLY to non-supervisors. A user who can
       *  see the team dashboard (`pages.team_overview`) gets a team-only
       *  view (no personal KPIs / tasks / reports / quick actions);
       *  everyone else gets this personal-only view. */}
      {!canSeeTeamOverview ? (
      <>
      <SectionHeader
        title="My Work Today"
        hint="What needs you next, plus how today is tracking."
      />
      {personalQuery.isLoading && !personal ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading your dashboard…
        </p>
      ) : personalError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">{personalError}</p>
      ) : personal ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Both tiles link straight to the Focus Board — the page
           *  whose Due Today / Overdue sections ARE these numbers. They
           *  used to point at /app/my-tasks?…, a removed page whose
           *  redirect landed here anyway but dropped the query string —
           *  so the hover/status-bar URL didn't match the destination
           *  (QA-reported) and the filter intent was silently lost. */}
          <KpiHeroTile
            to="/app/focus-board"
            icon={Clock}
            label="Due Today"
            value={personal.tasks.dueTodayCount}
            tone="rose"
          />
          <KpiHeroTile
            to="/app/focus-board"
            icon={AlertOctagon}
            label="Overdue"
            value={personal.tasks.overdueCount}
            tone="amber"
          />
          {/* To Do / Completed Tasks — the team view's tiles (same icon,
           *  tone and definition) over the user's OWN tasks (server:
           *  dashboardOverview toDoCount / completedCount). No "View" link:
           *  no page lists exactly these sets for one user. Hidden on an
           *  older API that doesn't send them. */}
          {personal.tasks.toDoCount != null ? (
            <KpiHeroTile
              icon={Layers}
              label="To Do"
              value={personal.tasks.toDoCount}
              tone="violet"
            />
          ) : null}
          {personal.tasks.completedCount != null ? (
            <KpiHeroTile
              icon={Trophy}
              label="Completed Tasks"
              value={personal.tasks.completedCount}
              tone="emerald"
              hint="All completed, incl. subtasks"
            />
          ) : null}
        </div>
      ) : null}

      {/* ──────────────────────────────────────────────────────────────────
       *  Your Numbers — personal analytics. Every chart is fed by data the
       *  dashboard already fetches (personal overview + my tasks); no extra
       *  API calls, no team/company data. Rendered once the personal
       *  overview has loaded OR failed — the My Work Today loader above
       *  covers the first wait. [QA-25] It used to need `personal`, so a
       *  failed overview call took every card with it, including the ones
       *  fed by the /me/tasks query that had succeeded; each card now
       *  shows its own error / loading state instead.
       *  Cards: Non-repeat / Repeat Task Completion pies, Workload by Space,
       *  My Momentum. (My Open Workload and Task Completion were removed on
       *  request, 2026-10-07.)
       *  ───────────────────────────────────────────────────────────── */}
      {personal || personalError ? (
        <>
          <SectionHeader
            title="Your Numbers"
            hint="How your workload and progress are tracking."
          />
          {/* Non-repeat / Repeat Task Completion — the user's OWN tasks
           *  (server: dashboard-overview tasks.repeatSplit, scope 'self'),
           *  same card + pie as the team view. Hidden (not zeroed) when the
           *  personal overview failed or an older API doesn't send it. */}
          {personal?.tasks?.repeatSplit ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RepeatSplitCard
                title="Non-repeat Task Completion"
                part={personal.tasks.repeatSplit.nonRepeat}
                scope={personal.tasks.repeatSplit.scope}
                icon={CheckSquare}
                tone="sky"
                empty="No non-repeat tasks yet."
              />
              <RepeatSplitCard
                title="Repeat Task Completion"
                part={personal.tasks.repeatSplit.repeat}
                scope={personal.tasks.repeatSplit.scope}
                icon={Repeat}
                tone="violet"
                empty="No repeat tasks yet."
              />
            </div>
          ) : null}
          {/* Workload by Space + My Last 14 Days share one row (on request,
           *  2026-10-07), stacked below lg. Workload by Space is fed by
           *  /me/tasks, so it still shows when the personal overview failed
           *  (the pies above are hidden then). */}
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4${personal?.tasks?.repeatSplit ? ' mt-4' : ''}`}>
            <MySpaceCard rows={myBySpace} loading={tasksLoading} error={tasksError} />
            <MyMomentumCard trend={myTrend} error={personalError} />
          </div>
        </>
      ) : null}

      {/* ──────────────────────────────────────────────────────────────────
       *  Today — two-up. Today's Focus (top 5 personal tasks queued for
       *  today) + Daily Report (three-state check-in).
       *  ───────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Today"
        hint="Where you are right now, and the daily check-in."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left — Today's Focus */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
              <span
                className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.violet.tile}`}
              >
                <ClipboardList size={20} />
              </span>
              <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                Today&apos;s Focus
              </h3>
            </div>
            <Link
              to="/app/focus-board"
              className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
            >
              View All
              <ArrowRight size={12} />
            </Link>
          </div>
          {tasksLoading ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          ) : tasksError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)] py-2">{tasksError}</p>
          ) : upNext.length === 0 ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
              No tasks queued up — enjoy the breathing room.
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--color-rule)] -mx-5">
              {upNext.map((t) => (
                <li key={t.id}>
                  <TaskRow task={t} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right — Daily Report */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
              <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.emerald.tile}`}>
                <FileText size={20} />
              </span>
              <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                Daily Report
              </h3>
            </div>
          </div>
          {personalQuery.isLoading && !personal ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          ) : dailyReport == null ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
              Daily report status unavailable right now.
            </p>
          ) : (
            <div className="space-y-3">
              {/* ── Today's daily ─────────────────────────────────
               *  submitted → green confirmation (whatever the schedule
               *  says — a voluntary weekend submission still shows).
               *  due today → amber prompt. Not a scheduled day → quiet
               *  neutral note instead of a nag for a report that was
               *  never due (QA: the highlight showed even when nothing
               *  was pending). */}
              {dailyReport.submitted ? (
                <div className="rounded-lg border border-[#A7F3D0] bg-[#ECFDF5] dark:border-emerald-500/20 dark:bg-emerald-500/[0.07] p-4">
                  <div className="flex items-start gap-3">
                    <span className="h-9 w-9 rounded-full bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/20 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#065F46] dark:text-emerald-200 m-0">
                        Submitted{dailyReport.submittedAt
                          ? ` at ${formatDateTime(dailyReport.submittedAt)}`
                          : ''}
                      </p>
                      <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)]">
                        You've checked in for today. Nice work.
                      </p>
                    </div>
                  </div>
                  {/* `/work-reports` is the admin-only Work Reports page —
                   *  for an Employee, "my submissions" lives on their daily
                   *  report page under the user panel. */}
                  <Link
                    to="/app/reports/daily"
                    className="mt-3 inline-flex items-center gap-1 text-[13px] font-medium text-[color:var(--color-accent-strong)] hover:underline"
                  >
                    View My Reports
                    <ArrowRight size={12} />
                  </Link>
                </div>
              ) : dailyReport.isRequired && dailyReport.dueToday ? (
                <div className="rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-4">
                  <div className="flex items-start gap-3">
                    <span className="h-9 w-9 rounded-full bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20 flex items-center justify-center shrink-0">
                      <ClipboardList size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
                        Today's daily report isn't submitted yet
                      </p>
                      <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)]">
                        A short check-in so your Manager can review the day.
                      </p>
                    </div>
                  </div>
                  {/* Daily-report submission for an Employee lives at
                   *  `/app/reports/daily`. `/work-reports` is the admin
                   *  Work Reports surface and isn't routed for users. */}
                  <Link
                    to="/app/reports/daily"
                    className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-medium bg-[color:var(--color-primary)] text-white shadow-sm hover:shadow hover:bg-[color:var(--color-primary-hover)] transition-all cursor-pointer"
                  >
                    <FileText size={13} />
                    Submit Daily Report
                  </Link>
                </div>
              ) : dailyReport.isRequired ? (
                <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4 flex items-start gap-3">
                  <span className="h-9 w-9 rounded-full bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)] flex items-center justify-center shrink-0">
                    <ClipboardCheck size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
                      No daily report due today
                    </p>
                    <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)]">
                      Today isn't one of your company's scheduled reporting
                      days.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
                  Daily report not required for your role.
                </p>
              )}

              {/* ── Previous scheduled day still open ─────────────
               *  Mirrors the grace-reminder email: the most recent
               *  scheduled working day whose daily was never filed. */}
              {dailyReport.isRequired &&
              dailyReport.previousDay &&
              !dailyReport.previousDay.submitted ? (
                <div className="rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-4">
                  <div className="flex items-start gap-3">
                    <span className="h-9 w-9 rounded-full bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20 flex items-center justify-center shrink-0">
                      <AlertTriangle size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0">
                        The daily report for{' '}
                        {fmtDayKeyLong(dailyReport.previousDay.date)} wasn't
                        submitted
                      </p>
                      <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-muted)]">
                        Catch up so the day doesn't stay blank on your
                        Manager's review.
                      </p>
                    </div>
                  </div>
                  {/* Deep-links the daily form to that date (`?date=`) so
                   *  the employee lands on the right day, not today's. */}
                  <Link
                    to={`/app/reports/daily?date=${dailyReport.previousDay.date}`}
                    className="mt-3 inline-flex items-center gap-2 h-9 px-4 rounded-full text-[13px] font-medium border border-[color:var(--color-warning-border)] bg-[color:var(--color-card)] text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
                  >
                    <FileText size={13} />
                    Submit for {fmtDayKeyShort(dailyReport.previousDay.date)}
                  </Link>
                </div>
              ) : null}

              {/* ── Weekly / monthly cadences ─────────────────────
               *  Appear once their reminder date arrives (end of week /
               *  month per the company's Report Schedule) and stay until
               *  the report is filed, then flip to a confirmation. */}
              <CadenceReminder
                icon={CalendarDays}
                label="Weekly"
                reminder={reportReminders?.weekly ?? null}
                to="/app/reports/weekly"
              />
              <CadenceReminder
                icon={CalendarRange}
                label="Monthly"
                reminder={reportReminders?.monthly ?? null}
                to="/app/reports/monthly"
              />
            </div>
          )}
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────────────
       *  For You — two-up. SOP Steps Assigned to Me + Comments & Mentions.
       *  ───────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="For You"
        hint="SOP steps and conversations waiting on you."
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left — SOP Steps Assigned to Me */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
              <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.amber.tile}`}>
                <BookOpen size={20} />
              </span>
              <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                SOP Steps Assigned to Me
              </h3>
            </div>
            <Link
              to="/app/sops"
              className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
            >
              SOP Library
              <ArrowRight size={12} />
            </Link>
          </div>
          {personalQuery.isLoading && !personal ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          ) : personalError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
              {personalError}
            </p>
          ) : sopSteps.length === 0 ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
              No SOP steps assigned right now.
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--color-rule)] -mx-5">
              {sopSteps.slice(0, 5).map((step) => {
                const StatusIcon =
                  step.status === 'done'
                    ? CheckCircle2
                    : step.status === 'in_progress'
                      ? Play
                      : Circle
                const statusColor =
                  step.status === 'done'
                    ? '#059669'
                    : step.status === 'in_progress'
                      ? '#2563EB'
                      : '#94A3B8'
                const statusLabel =
                  step.status === 'done'
                    ? 'Done'
                    : step.status === 'in_progress'
                      ? 'In progress'
                      : 'Pending'
                const statusChipCls =
                  step.status === 'done'
                    ? 'bg-[#D1FAE5] text-[#059669] border-[#A7F3D0]'
                    : step.status === 'in_progress'
                      ? 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE]'
                      : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]'
                const href = step.taskId
                  ? `/app/focus-board?taskId=${step.taskId}`
                  : '/app/sops'
                return (
                  <li key={step.stepId}>
                    <Link
                      to={href}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
                    >
                      <StatusIcon size={16} style={{ color: statusColor }} className="shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-normal text-[color:var(--color-ink-strong)] truncate">
                          {step.stepTitle}
                        </p>
                        <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate">
                          from {step.sopName}
                        </p>
                      </div>
                      <span
                        className={
                          'inline-flex items-center px-2 py-0.5 rounded-full border text-[13px] font-normal tabular-nums shrink-0 ' +
                          statusChipCls
                        }
                      >
                        {statusLabel}
                      </span>
                      {step.dueDate ? <DueChip iso={step.dueDate} /> : null}
                    </Link>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Right — Comments & Mentions (comments on your tasks, replies to
         *  your comments, and @-mentions of you — each row's verb reflects
         *  its type; the card title is the accurate umbrella). */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <div className="flex items-center gap-2">
              <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.sky.tile}`}>
                <MessageSquare size={20} />
              </span>
              <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                Comments &amp; Mentions
              </h3>
            </div>
            <Link
              to="/app/notifications"
              className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
            >
              View All
              <ArrowRight size={12} />
            </Link>
          </div>
          {personal ? (
            personal.mentions.length === 0 ? (
              <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
                No new comments or mentions — you're all caught up.
              </p>
            ) : (
              <ul className="space-y-3">
                {personal.mentions.slice(0, 5).map((m) => {
                  const taskName =
                    m.taskName.length > 30
                      ? m.taskName.slice(0, 30) + '…'
                      : m.taskName
                  return (
                    <li key={m.id} className="flex items-start gap-3">
                      <Avatar
                        name={m.fromUserName}
                        avatarUrl={m.fromAvatarUrl}
                        size="sm"
                        colorByName
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                          <span className="font-medium">{m.fromUserName}</span>
                          <span className="text-[color:var(--color-ink-muted)] font-normal">
                            {m.type === 'reply'
                              ? ' replied to your comment on '
                              : m.type === 'comment'
                                ? ' commented on '
                                : ' mentioned you on '}
                          </span>
                          <span className="font-medium">{taskName}</span>
                        </p>
                        <p className="mt-0.5 text-sm text-[color:var(--color-ink-muted)] truncate">
                          {m.snippet}
                        </p>
                        <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-subtle)] tabular-nums">
                          {fmtRelative(m.createdAt)}
                        </p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )
          ) : personalQuery.isLoading ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </p>
          ) : personalError ? (
            // [QA-25] Was a blank card (header only) when the overview failed.
            <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
              {personalError}
            </p>
          ) : null}
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────────────
       *  Recent Activity — single combined feed of newly assigned tasks,
       *  tasks I completed today, recent mentions, and SOP steps that
       *  moved into in_progress. Capped at 8 rows.
       *  ───────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Recent Activity"
        hint="Assignments, completions, mentions, and SOP progress."
      />
      {/* `overflow-hidden` + the list's full-bleed negative margins keep
       *  the row dividers and hover fills flush with the card edges —
       *  the old p-6 + -mx-5 combination left empty padding strips
       *  above/below the list and a 4px inset either side (QA-flagged). */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 overflow-hidden">
        {(personalQuery.isLoading || tasksLoading) && activityFeed.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2 py-2">
            <Loader2 size={14} className="animate-spin" /> Loading activity…
          </p>
        ) : (personalError || tasksError) && activityFeed.length === 0 ? (
          // Both feeds behind this card failed (or the one with data did) —
          // say so instead of claiming there's simply no activity.
          <p className="text-sm text-[color:var(--color-priority-urgent)] py-6 text-center">
            {personalError ?? tasksError}
          </p>
        ) : activityFeed.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] italic py-6 text-center">
            No recent activity to surface. New assignments, mentions, and
            completions will land here.
          </p>
        ) : (
          <ul className="divide-y divide-[color:var(--color-rule)] -mx-6 -my-6">
            {activityFeed.map((item) => {
              const Icon = item.icon
              const tone = TONE_CLASSES_HERO[item.tone] ?? TONE_CLASSES_HERO.sky
              return (
                <li key={item.id}>
                  <Link
                    to={item.href}
                    className="flex items-start gap-3 px-6 py-3 hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
                  >
                    {item.avatarName ? (
                      <Avatar
                        name={item.avatarName}
                        avatarUrl={item.avatarUrl}
                        size="sm"
                        colorByName
                      />
                    ) : (
                      <span className={`h-7 w-7 rounded-lg shrink-0 flex items-center justify-center ${tone.tile}`}>
                        <Icon size={14} />
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                        <span className="font-medium">{item.primary}</span>
                        <span className="text-[color:var(--color-ink-muted)] font-normal">
                          {' '}
                          {item.secondary}
                        </span>
                      </p>
                      {item.ts ? (
                        <p className="mt-0.5 text-[13px] text-[color:var(--color-ink-subtle)] tabular-nums">
                          {fmtRelative(item.ts)}
                        </p>
                      ) : null}
                    </div>
                    {/* `self-center` — the chevron centres against the
                     *  whole (usually two-line) row instead of hugging
                     *  the title line with a fixed mt nudge. */}
                    <ArrowRight
                      size={14}
                      className="text-[color:var(--color-ink-subtle)] shrink-0 self-center"
                    />
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────────────────
       *  Personal Quick Actions — primary jumps for everyone. The
       *  Leaderboard shortcut surfaces XP / badges / standing.
       *  ───────────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Quick Actions"
        hint="Jump straight into the things you do most."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ShortcutLink
          to="/app/reports/daily"
          icon={FileText}
          label="Submit Daily Report"
          description="One-paragraph check-in for your Manager."
          tone="emerald"
        />
        <ShortcutLink
          to="/app/sops"
          icon={BookOpen}
          label="Open SOP Library"
          description="Procedures, playbooks, and templates."
          tone="amber"
        />
        <ShortcutLink
          to="/app/files"
          icon={Upload}
          label="Files & Links"
          description="Browse and upload your files and links."
          tone="sky"
        />
        <ShortcutLink
          to="/app/leaderboard"
          icon={Trophy}
          label="Leaderboard"
          description="Your XP, badges, and team standing."
          tone="gold"
        />
      </div>
      </>
      ) : null}

      {/* ════════════════════════════════════════════════════════════════
       *  TEAM / SUPERVISION SECTION — rendered only for users who hold
       *  `pages.team_overview` (the operation that authorises the
       *  workspace-overview data behind every widget here). Each widget
       *  is then further gated on its own specific operation.
       *  ══════════════════════════════════════════════════════════════ */}
      {canSeeTeamOverview ? (
        <>
          {/* ──────────────────────────────────────────────────────────────
           *  Team Supervision — hero KPI strip: To Do /
           *  Completed Tasks / Reports Submitted This Week / Team
           *  Members.
           *  ─────────────────────────────────────────────────────────── */}
          <SectionHeader
            title="Team Supervision"
            hint="Live numbers across the team you oversee."
            action={overview ? <ScopePill scope={overview.scope} /> : null}
          />
          {teamLoading && !overview ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading team data…
            </p>
          ) : teamError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)]">{teamError}</p>
          ) : overview ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Link to the plain Team Tasks page (no `?status=open`
               *  pre-filter). The "open" filter hid any completed PARENT
               *  task while still counting its open subtasks, so the
               *  list could land empty and only fill in after a refresh
               *  (which dropped the stripped param). Showing the full,
               *  unfiltered list on click is what the user expects from
               *  this tile and avoids that empty-then-refresh surprise. */}
              <KpiHeroTile
                to="/app/team-tasks"
                icon={Layers}
                label="To Do"
                value={overview.tasks.openCount}
                tone="violet"
              />
              <KpiHeroTile
                to="/app/team-tasks?status=complete"
                icon={Trophy}
                label="Completed Tasks"
                value={overview.tasks.completedCount}
                tone="emerald"
                // All-time completions across the team, INCLUDING subtasks
                // (counted by their own status) and every "done" workflow
                // state. The Team Tasks page groups by status and its column
                // headers count top-level tasks only, so this total is
                // intentionally higher than any single COMPLETE column.
                hint="All completed, incl. subtasks"
              />
              <KpiHeroTile
                to="/app/reports/team"
                icon={FileText}
                label="Reports Submitted · This Week"
                value={overview.reportsThisWeek}
                tone="amber"
              />
              <KpiHeroTile
                to="/app/team-overview"
                icon={Users}
                label="Team Members"
                value={overview.members.total}
                tone="sky"
              />
            </div>
          ) : null}

          {/* ──────────────────────────────────────────────────────────────
           *  Analytics — department Non-repeat / Repeat Task Completion
           *  pies, Team by Work Role donut, Workload by Space bars. (The
           *  Completion ring and Tasks by Status donut were removed on
           *  request, 2026-10-07.)
           *  ─────────────────────────────────────────────────────────── */}
          {showAnalytics ? (
            <SectionHeader
              title="Analytics"
              hint="Task completion, team make-up, and workload at a glance."
            />
          ) : null}
          {/* Gate the cards on `overview` the way the KPI strip above does, so
           *  they show a loader while team data is in flight and an error line
           *  on a failed fetch — rather than each card asserting "no data"
           *  during a load or a failed fetch. [QA-25] On error this used to
           *  render nothing under the "Analytics" header. */}
          {showAnalytics && teamLoading && !overview ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading analytics…
            </p>
          ) : showAnalytics && teamError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              Team analytics couldn&apos;t be loaded. {teamError}
            </p>
          ) : showAnalytics && overview ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Department-only Non-repeat / Repeat pies. Hidden (not zeroed)
               *  against an older API that doesn't send `repeatSplit`. */}
              {overview?.tasks?.repeatSplit ? (
                <>
                  <RepeatSplitCard
                    title="Non-repeat Task Completion"
                    part={overview.tasks.repeatSplit.nonRepeat}
                    scope={overview.tasks.repeatSplit.scope}
                    icon={CheckSquare}
                    tone="sky"
                    empty="No non-repeat tasks yet."
                  />
                  <RepeatSplitCard
                    title="Repeat Task Completion"
                    part={overview.tasks.repeatSplit.repeat}
                    scope={overview.tasks.repeatSplit.scope}
                    icon={Repeat}
                    tone="violet"
                    empty="No repeat tasks yet."
                  />
                </>
              ) : null}
              <TeamWorkRoleCard byWorkRole={overview?.members?.byWorkRole ?? []} />
              <WorkloadBySpaceCard
                bySpace={overview?.tasks?.bySpace ?? []}
                otherSpacesOpen={overview?.tasks?.otherSpacesOpen ?? 0}
              />
            </div>
          ) : null}

          {/* ──────────────────────────────────────────────────────────────
           *  Team Momentum — Top Performers leaderboard (30 days) beside
           *  Team Throughput (points, completions, reports, badges).
           *  ─────────────────────────────────────────────────────────── */}
          {showTeamMomentum ? (
            <>
              <SectionHeader
                title="Team Momentum"
                hint="Who's earning points, and how much the team is getting done."
              />
              {teamLoading && !overview ? (
                <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading momentum…
                </p>
              ) : teamError && !overview ? (
                <p className="text-sm text-[color:var(--color-priority-urgent)]">
                  Team momentum couldn&apos;t be loaded. {teamError}
                </p>
              ) : overview ? (
                // Side by side only from xl: at lg (1024–1279px, with the
                // 280px sidebar) the 2/5 column is too narrow for 2×2 tiles.
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                  <div className="xl:col-span-3">
                    <TeamTopPerformersCard
                      performers={overview.topPerformersLast30}
                      teamPoints={overview.pointsLast30}
                      currentUserId={user?.id}
                      showLeaderboardLink={hasGamification}
                    />
                  </div>
                  <div className="xl:col-span-2">
                    <TeamThroughputCard
                      overview={overview}
                      reportsHref={canViewTeamReports ? '/app/reports/team' : undefined}
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {/* ──────────────────────────────────────────────────────────────
           *  Action Needed — overdue across the team, plus the
           *  members-behind-on-reports / inactive-today reminder cards.
           *  ─────────────────────────────────────────────────────────── */}
          {showActionNeeded ? (
            <SectionHeader
              title="Action Needed"
              hint="High-priority triage for today."
            />
          ) : null}

          {/* Overdue Across Team — gated on `overview` (like the KPI strip
           *  and the Analytics row) so this triage card never claims "0 —
           *  Nothing overdue" while team data is loading or after a failed
           *  fetch; the hero above already surfaces `teamError`. */}
          {teamLoading && !overview ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading overdue tasks…
            </p>
          ) : teamError && showActionNeeded ? (
            // [QA-25] The "Action Needed" header used to sit over nothing.
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              Overdue tasks couldn&apos;t be loaded. {teamError}
            </p>
          ) : overview ? (
            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 relative overflow-hidden">
                <div
                  aria-hidden="true"
                  className={
                    'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
                    TONE_CLASSES_HERO.rose.halo
                  }
                />
                <div className="relative">
                  <div className="flex items-baseline justify-between mb-3 gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${TONE_CLASSES_HERO.rose.tile}`}>
                        <AlertOctagon size={20} />
                      </span>
                      <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
                        Overdue Across Team
                      </h3>
                    </div>
                    <Link
                      to="/app/team-tasks?filter=overdue"
                      className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
                    >
                      View All
                      <ArrowRight size={12} />
                    </Link>
                  </div>
                  <p className="font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
                    {overview?.tasks.overdueCount ?? 0}
                  </p>
                  <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
                    {(overview?.tasks.overdueCount ?? 0) === 1
                      ? 'task past its due date'
                      : 'tasks past their due date'}
                  </p>
                  {overview && overview.overdueTopTasks.length > 0 ? (
                    <ul className="mt-3 divide-y divide-[color:var(--color-rule)] -mx-5 border-t border-[color:var(--color-rule)]">
                      {overview.overdueTopTasks.slice(0, 5).map((t) => (
                        <li key={t.taskId}>
                          <Link
                            to={
                              t.spaceId
                                ? `/app/spaces/${t.spaceId}/lists/${t.listId}`
                                : '/app/team-tasks?filter=overdue'
                            }
                            className="flex items-center gap-3 px-5 py-2.5 hover:bg-[color:var(--color-card-hover)] transition-colors cursor-pointer"
                          >
                            <span className="h-2 w-2 rounded-full shrink-0 bg-[color:var(--color-danger)]" aria-hidden="true" />
                            <p className="flex-1 min-w-0 text-sm text-[color:var(--color-ink-strong)] truncate">
                              {t.taskName}
                            </p>
                            <DueChip iso={t.dueDate} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-[13px] text-[color:var(--color-ink-muted)] italic">
                      Nothing overdue — the team is on track.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* ──────────────────────────────────────────────────────────────
           *  Team Quick Actions — common supervision jumps. Each shortcut
           *  is gated on its own operation.
           *  ─────────────────────────────────────────────────────────── */}
          {showTeamQuickActions ? (
            <>
              <SectionHeader
                title="Team Actions"
                hint="Common jumps for managing the team."
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {canSeeTeamTasks ? (
                  <ShortcutLink
                    to="/app/team-tasks"
                    icon={Activity}
                    label="Open Team Tasks"
                    description="Live view of who's working on what right now."
                    tone="rose"
                  />
                ) : null}
                {canSeeTeamTasks && canDelegate ? (
                  <ShortcutLink
                    to={assignTaskHref}
                    icon={PlusCircle}
                    label="Assign Task"
                    description="Open a task list to create and assign a new task."
                    tone="violet"
                  />
                ) : null}
                {canDelegate ? (
                  <ShortcutLink
                    to="/app/team-tasks"
                    icon={Scale}
                    label="Reassign Workload"
                    description="Rebalance open tasks across the team."
                    tone="sky"
                  />
                ) : null}
                {canViewTeamReports ? (
                  <ShortcutLink
                    to="/app/reports/team"
                    icon={ClipboardCheck}
                    label="Review Reports"
                    description="Open the team's submitted work reports."
                    tone="emerald"
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

// ============================================================================
// Shared computation helpers used across the dashboard's surfaces
// ============================================================================

function sortUpNext(tasks) {
  return [...tasks]
    .filter((t) => t.completedAt === null || t.completedAt === undefined)
    .sort((a, b) => {
      const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY
      const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY
      if (aDue !== bDue) return aDue - bDue
      const aPri = a.priority ? PRIORITY_WEIGHT[a.priority] : 4
      const bPri = b.priority ? PRIORITY_WEIGHT[b.priority] : 4
      return aPri - bPri
    })
}

// `TopPerformersList` removed in the May-2026 sweep — the team
// momentum feed surfaces the leaderboard differently now (inline
// avatar row) and the standalone list had no remaining callers.
